import React, { useState, useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { LoaderCircle } from 'lucide-react';
import { CategorySpentData } from '../../../utils/chartsUtils';
import { getCategoryTextColor } from '../../orderhistory/categoryUtils';

ChartJS.register(ArcElement, Tooltip, Legend);

interface CategorySpentChartProps {
  data: CategorySpentData[];
  loading?: boolean;
}

export const CategorySpentChart: React.FC<CategorySpentChartProps> = ({ data, loading }) => {
  const { t } = useTranslation();
  const chartRef = useRef<ChartJS<'doughnut'>>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  if (data.length === 0) {
    return (
      <div className="bg-white dark:bg-card-dark p-4 rounded-[12px] relative shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040]">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <LoaderCircle className="h-8 w-8 animate-spin text-textPrimary dark:text-textPrimary-dark" />
          </div>
        ) : (
          <p className="text-[#475467] dark:text-textSecondary-dark text-center py-8">{t('charts.noData')}</p>
        )}
      </div>
    );
  }

  // Use category colors from getCategoryTextColor
  const colors = data.map(item => getCategoryTextColor(item.categoryKey));
  const chartData: ChartData<'doughnut'> = {
    labels: data.map(item => item.category),
    datasets: [
      {
        data: data.map(item => item.percentage),
        backgroundColor: colors,
        borderWidth: 0,
        borderRadius: 10,
        spacing: 6,
        hoverOffset: 2,
      },
    ],
  };

  const options: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: (context) => {
          let tooltipEl = document.getElementById('category-chartjs-tooltip');

          if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'category-chartjs-tooltip';
            tooltipEl.innerHTML = `
              <div class="bg-white dark:bg-background-dark border border-[#F5F5F5] dark:border-border-dark rounded-lg p-2 shadow-lg">
                <span class="tooltip-text"></span>
              </div>
            `;
            document.body.appendChild(tooltipEl);
          }

          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = '0';
            return;
          }

          if (tooltipModel.body) {
            const textSpan = tooltipEl.querySelector('.tooltip-text') as HTMLElement;
            const dataIndex = tooltipModel.dataPoints[0].dataIndex;
            const item = data[dataIndex];

            textSpan.innerHTML = `${item.category} ${item.percentage.toFixed(1)}%`;
            textSpan.style.color = colors[dataIndex];
          }

          const position = context.chart.canvas.getBoundingClientRect();

          tooltipEl.style.opacity = '1';
          tooltipEl.style.position = 'absolute';
          tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX - 30 + 'px';
          tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY - 40 + 'px';
          tooltipEl.style.pointerEvents = 'none';
          tooltipEl.style.zIndex = '100';
          tooltipEl.style.transition = 'all 0.1s ease';
          tooltipEl.style.transform = 'translateX(-50%)';
        }
      }
    },
  };

  return (
    <>
      <div className="bg-white dark:bg-card-dark p-4 rounded-[12px] relative shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040]">
        <div className='mb-8 md:mb-16 w-full flex items-center justify-between gap-4'>
          <div>
            <h1 className='text-[#101828] dark:text-textPrimary-dark text-[20px] leading-[30px] font-bold mb-1'>{t('charts.categorySpendBreakdownTitle')}</h1>
            <p className='text-[#475467] dark:text-textSecondary-dark text-[14px] leading-[21px] font-normal'>{t('charts.categorySpendBreakdownSubtitle')}</p>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className='border border-[#D0D5DD] dark:border-borderPrimary-dark rounded-[10px] h-10 pl-2 pr-3 hidden md:flex items-center gap-1 hover:bg-gray-100 dark:hover:bg-backgroundDarker-dark transition-all duration-300'
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#475467] dark:text-textSecondary-dark">
              <path d="M5.83317 14.1667H8.33317C8.56928 14.1667 8.7672 14.2466 8.92692 14.4063C9.08664 14.5661 9.1665 14.764 9.1665 15.0001C9.1665 15.2362 9.08664 15.4341 8.92692 15.5938C8.7672 15.7536 8.56928 15.8334 8.33317 15.8334H4.99984C4.76373 15.8334 4.56581 15.7536 4.40609 15.5938C4.24637 15.4341 4.1665 15.2362 4.1665 15.0001V11.6667C4.1665 11.4306 4.24637 11.2327 4.40609 11.073C4.56581 10.9133 4.76373 10.8334 4.99984 10.8334C5.23595 10.8334 5.43387 10.9133 5.59359 11.073C5.75331 11.2327 5.83317 11.4306 5.83317 11.6667V14.1667ZM14.1665 5.83341H11.6665C11.4304 5.83341 11.2325 5.75355 11.0728 5.59383C10.913 5.43411 10.8332 5.23619 10.8332 5.00008C10.8332 4.76397 10.913 4.56605 11.0728 4.40633C11.2325 4.24661 11.4304 4.16675 11.6665 4.16675H14.9998C15.2359 4.16675 15.4339 4.24661 15.5936 4.40633C15.7533 4.56605 15.8332 4.76397 15.8332 5.00008V8.33342C15.8332 8.56953 15.7533 8.76744 15.5936 8.92717C15.4339 9.08689 15.2359 9.16675 14.9998 9.16675C14.7637 9.16675 14.5658 9.08689 14.4061 8.92717C14.2464 8.76744 14.1665 8.56953 14.1665 8.33342V5.83341Z" fill="currentColor" />
            </svg>
            <span className='text-[#667085] dark:text-textQuaternary-dark text-[14px] leading-5 font-semibold'>Expand</span>
          </button>
        </div>

        <div className='relative w-full h-[200px] sm:h-[240px] flex items-center justify-center select-none'>
          <Doughnut ref={chartRef} data={chartData} options={options} />
        </div>

        <div className="mt-11 mb-3 md:mb-6 flex items-center flex-wrap justify-center max-w-[424px] mx-auto gap-3">
          {data.map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-[3px]"
                style={{ backgroundColor: colors[index] }}
              />
              <span className="text-[#475467] dark:text-textSecondary-dark text-[13px] leading-5 font-medium whitespace-nowrap">
                {item.category} ({item.percentage.toFixed(1)}%)
              </span>
            </div>
          ))}
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#16192466] dark:bg-[#16192466]" onClick={() => setIsModalOpen(false)}>
          <div className="bg-white dark:bg-card-dark rounded-xl w-full max-w-[850px] p-5 relative" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-[#101828] dark:text-textPrimary-dark text-[18px] leading-[23px] font-semibold mb-1">{t('charts.categorySpendBreakdownTitle')}</h2>
                <p className="text-[#475467] dark:text-textSecondary-dark text-[14px] leading-[18px] font-normal">{t('charts.categorySpendBreakdownSubtitle')}</p>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="border border-[#D0D5DD] dark:border-borderPrimary-dark rounded-[10px] h-10 pl-2 pr-3 flex items-center gap-1 hover:bg-gray-100 dark:hover:bg-backgroundDarker-dark transition-all duration-300"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#475467] dark:text-textSecondary-dark">
                  <path d="M7.49984 12.5001H4.99984C4.76373 12.5001 4.56581 12.4202 4.40609 12.2605C4.24637 12.1008 4.1665 11.9029 4.1665 11.6667C4.1665 11.4306 4.24637 11.2327 4.40609 11.073C4.56581 10.9133 4.76373 10.8334 4.99984 10.8334H8.33317C8.56928 10.8334 8.7672 10.9133 8.92692 11.073C9.08664 11.2327 9.1665 11.4306 9.1665 11.6667V15.0001C9.1665 15.2362 9.08664 15.4341 8.92692 15.5938C8.7672 15.7536 8.56928 15.8334 8.33317 15.8334C8.09706 15.8334 7.89914 15.7536 7.73942 15.5938C7.5797 15.4341 7.49984 15.2362 7.49984 15.0001V12.5001ZM12.4998 7.50008H14.9998C15.2359 7.50008 15.4339 7.57994 15.5936 7.73966C15.7533 7.89939 15.8332 8.0973 15.8332 8.33342C15.8332 8.56953 15.7533 8.76744 15.5936 8.92717C15.4339 9.08689 15.2359 9.16675 14.9998 9.16675H11.6665C11.4304 9.16675 11.2325 9.08689 11.0728 8.92717C10.913 8.76744 10.8332 8.56953 10.8332 8.33342V5.00008C10.8332 4.76397 10.913 4.56605 11.0728 4.40633C11.2325 4.24661 11.4304 4.16675 11.6665 4.16675C11.9026 4.16675 12.1005 4.24661 12.2603 4.40633C12.42 4.56605 12.4998 4.76397 12.4998 5.00008V7.50008Z" fill="currentColor" />
                </svg>
                <span className="text-[#667085] dark:text-textQuaternary-dark text-[14px] leading-5 font-semibold">Collapse</span>
              </button>
            </div>
            <div className="w-full h-[350px] flex items-center justify-center select-none">
              <Doughnut data={chartData} options={options} />
            </div>
            <div className="mb-2 mt-6 flex items-center flex-wrap justify-center gap-3">
              {data.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div
                    className="w-4 h-4 rounded-[4px]"
                    style={{ backgroundColor: colors[index] }}
                  />
                  <span className="text-[#475467] dark:text-textSecondary-dark text-sm font-medium whitespace-nowrap">
                    {item.category} ({item.percentage.toFixed(1)}%)
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
